import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { addPurchaseRequestsUrl } from "../../../../utils/urls";
import { GET_DEPARTMENT_URL } from "../../../../utils/urls";
import { getItem } from "../../../../utils/localStorage";

const AddPurchaseRequest = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const [department, setDepartment] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [items, setItems] = useState([]);
  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false);

  // Fetch current user data when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; 
        }

        // Safely access and set user details
        if (parsedUser?.user) {
          const fullName = parsedUser.user.fullName;
          const role = parsedUser.user.role;

          setCurrentUser(parsedUser.user);
          setFullName(fullName);
          setRole(role);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log(err.message);
      }
    };

    getCurrentUser();
  }, []);

  // Fetch departments when facility is selected
  useEffect(() => {
    if (!selectedFacilityId) return;

    const fetchDepartments = async () => {
      setIsDepartmentsLoading(true);
      try {
        const response = await makeRequest2(
          `${GET_DEPARTMENT_URL}/${selectedFacilityId}`,
          "GET"
        );

        if (response.success && response.data) {
          const departmentData = response.data.data || response.data;
          const departmentsArray = Array.isArray(departmentData)
            ? departmentData
            : [departmentData];
          
          // Transform departments to dropdown format
          const formattedDepartments = departmentsArray.map(dept => ({
            label: dept.name,
            value: dept.name
          }));
          
          setDepartmentOptions(formattedDepartments);
        } else {
          console.error("Failed to fetch departments:", response);
          toastify("Failed to load departments", "error");
          // Fallback to empty array if fetch fails
          setDepartmentOptions([]);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        toastify("Error fetching departments", "error");
        // Fallback to empty array if fetch fails
        setDepartmentOptions([]);
      } finally {
        setIsDepartmentsLoading(false);
      }
    };

    fetchDepartments();
  }, [selectedFacilityId]);

  // Fetch inventory items when facility is selected
  useEffect(() => {
    if (!selectedFacilityId) return;

    const fetchInventoryItems = async () => {
      setIsLoading(true);
      try {
        const response = await makeRequest2(
          `/api/stockandspare/${selectedFacilityId}`,
          "GET"
        );

        // Check different possible response structures
        if (response && response.data && response.data.stocks) {
          setInventoryOptions(response.data.stocks);
        } else if (response && response.stocks) {
          setInventoryOptions(response.stocks);
        } else {
          console.error("Unexpected API response format:", response);
          toastify("Failed to load inventory items", "error");
        }
      } catch (error) {
        console.error("Error fetching inventory items:", error);
        toastify("Error fetching inventory items", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInventoryItems();
  }, [selectedFacilityId]);

  // If no facility is selected, display a message and exit early.
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="text-center p-5">Please select a facility first.</div>
      </Layout>
    );
  }

  // Add a new row for an inventory item.
  const handleAddInventoryItem = () => {
    setItems((prev) => [
      ...prev,
      {
        isInventoryItem: true,
        inventoryItemId: "",
        itemDescription: "", 
        quantity: 1,
        unitOfMeasure: "",
        remarksSpecification: "",
      },
    ]);
  };

  // Add a new row for a new (manual) item.
  const handleAddNewItem = () => {
    setItems((prev) => [
      ...prev,
      {
        isInventoryItem: false,
        inventoryItemId: "",
        itemDescription: "",
        quantity: 1,
        unitOfMeasure: "",
        remarksSpecification: "",
      },
    ]);
  };

  // Handle changes for each item field
  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const updatedItems = [...prev];

      // Special handling for inventory item selection
      if (field === "inventoryItemId" && value) {
        const selectedInventory = inventoryOptions.find(item => item._id === value);
        if (selectedInventory) {
          updatedItems[index] = {
            ...updatedItems[index],
            inventoryItemId: value,
            itemDescription: selectedInventory.name, // Auto-fill description from inventory
            unitOfMeasure: selectedInventory.unit || updatedItems[index].unitOfMeasure, // Auto-fill unit if available
          };
          return updatedItems;
        }
      }

      // Normal field update
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
      return updatedItems;
    });
  };

  // Remove an item row
  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Clear the form
  const handleClear = () => {
    setDepartment("");
    setItems([]);
  };

  // Submit the purchase request
  const handleSubmit = async () => {
    if (!department) {
      toastify("Please select a department", "error");
      return;
    }

    if (items.length === 0) {
      toastify("Please add at least one item", "error");
      return;
    }

    // Validate required fields
    for (const item of items) {
      if (item.isInventoryItem && !item.inventoryItemId) {
        toastify("Please select an inventory item", "error");
        return;
      }
      if (!item.isInventoryItem && !item.itemDescription) {
        toastify("Please enter item description", "error");
        return;
      }
      if (!item.unitOfMeasure) {
        toastify("Please select unit of measure for all items", "error");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Format the payload to match the updated schema
      const payload = {
        department,
        userName: fullName || "",
        items: items.map((item) => ({
          itemDescription: item.itemDescription,
          quantity: parseInt(item.quantity) || 1,
          unitOfMeasure: item.unitOfMeasure,
          remarksSpecification: item.remarksSpecification || "",
          // Keep track of inventory item reference if needed for future use
          inventoryItemId: item.isInventoryItem ? item.inventoryItemId : null,
          isInventoryItem: item.isInventoryItem
        })),
      };

      const response = await makeRequest2(
        `${addPurchaseRequestsUrl}/${selectedFacilityId}`,
        "POST",
        payload
      );

      // Adjust success check based on your API response format
      if (response && response.success === false) {
        toastify(response.error || "Failed to create purchase request", "error");
        return;
      }

      toastify(response.message || "Purchase Request added successfully", "success");
      handleClear();
      navigate("/facility/procurement_management/purchase_requests");
    } catch (error) {
      toastify("An unexpected error occurred: " + (error.message || "Unknown error"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/purchase_requests">
                    Purchase Requests
                  </Link>
                </li>
                <li className="breadcrumb-item">Add Purchase Request</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="/facility/procurement_management/purchase_requests">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5>Add Purchase Request</h5>
              {fullName && (
                <div className="text-muted">
                  From: {fullName} {role ? `(${role})` : ''}
                </div>
              )}
            </div>
            <div className="card-body">
              {/* Loading indicator for departments */}
              {isDepartmentsLoading && (
                <div className="text-center mb-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading departments...</p>
                </div>
              )}

              {/* Loading indicator for inventory */}
              {isLoading && (
                <div className="text-center mb-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading inventory items...</p>
                </div>
              )}

              {/* Department Field */}
              <div className="mb-4">
                <label className="form-label">Department <span className="text-danger">*</span></label>
                <Dropdown
                  value={department}
                  options={departmentOptions}
                  onChange={(e) => setDepartment(e.value)}
                  placeholder={
                    isDepartmentsLoading ? "Loading departments..." : 
                    departmentOptions.length === 0 ? "No departments available" : 
                    "Select Department"
                  }
                  className="w-100"
                  disabled={isSubmitting || isDepartmentsLoading || departmentOptions.length === 0}
                />
                {!isDepartmentsLoading && departmentOptions.length === 0 && (
                  <small className="text-muted">
                    No departments found for this facility. Please add departments in the facility settings first.
                  </small>
                )}
              </div>

              {/* Items Section */}
              <h5>Items</h5>
              <div className="mb-3">
                <Button
                  label="Add Inventory Item"
                  className="p-button-outlined p-button-success me-2"
                  onClick={handleAddInventoryItem}
                  disabled={isSubmitting || isLoading}
                />
                <Button
                  label="Add New Item"
                  className="p-button-outlined p-button-info"
                  onClick={handleAddNewItem}
                  disabled={isSubmitting}
                />
              </div>

              {/* Items Table */}
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: "30%" }}>Item Description <span className="text-danger">*</span></th>
                      <th style={{ width: "10%" }}>Quantity <span className="text-danger">*</span></th>
                      <th style={{ width: "15%" }}>Unit of Measure <span className="text-danger">*</span></th>
                      <th style={{ width: "35%" }}>Remarks / Specifications</th>
                      <th style={{ width: "5%" }}>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        {/* Item Description or Inventory Selection */}
                        <td>
                          {item.isInventoryItem ? (
                            <Dropdown
                              value={item.inventoryItemId}
                              options={inventoryOptions.map((inv) => ({
                                label: inv.name,
                                value: inv._id,
                              }))}
                              onChange={(e) =>
                                handleItemChange(index, "inventoryItemId", e.value)
                              }
                              placeholder="Select Inventory Item"
                              className="w-100"
                              disabled={isSubmitting || isLoading}
                            />
                          ) : (
                            <InputText
                              value={item.itemDescription}
                              onChange={(e) =>
                                handleItemChange(index, "itemDescription", e.target.value)
                              }
                              placeholder="Enter item description"
                              className="w-100"
                              disabled={isSubmitting}
                            />
                          )}
                        </td>

                        {/* Quantity */}
                        <td>
                          <InputText
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(index, "quantity", parseInt(e.target.value) || 1)
                            }
                            min="1"
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Unit of Measure */}
                        <td>
                          <Dropdown
                            value={item.unitOfMeasure}
                            options={[
                              { label: "Pieces", value: "Pieces" },
                              { label: "Pcs", value: "Pcs" },
                              { label: "Litres", value: "Litres" },
                              { label: "Box", value: "Box" },
                              { label: "Kg", value: "Kg" },
                              { label: "Meter", value: "Meter" },
                              { label: "Roll", value: "Roll" },
                              { label: "Pack", value: "Pack" },
                              { label: "Set", value: "Set" },
                              { label: "Unit", value: "Unit" },
                            ]}
                            onChange={(e) =>
                              handleItemChange(index, "unitOfMeasure", e.value)
                            }
                            placeholder="Select Unit"
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Remarks / Specifications */}
                        <td>
                          <InputText
                            value={item.remarksSpecification}
                            onChange={(e) =>
                              handleItemChange(index, "remarksSpecification", e.target.value)
                            }
                            placeholder="Remarks / Specifications"
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Remove Button */}
                        <td className="text-center">
                          <Button
                            icon="ti ti-trash"
                            className="p-button-rounded p-button-danger mr-2"
                            onClick={() => handleRemoveItem(index)}
                            disabled={isSubmitting}
                          />
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No items added. Click "Add Inventory Item" or "Add New Item" to begin.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={handleClear}
                  disabled={isSubmitting}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || items.length === 0 || !department}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddPurchaseRequest;